import {
  Button,
  Divider,
  Drawer,
  Form,
  Grid,
  Icons,
  InputNumber,
  Select,
  Space,
  Spin,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { IDamagedProduct } from 'pages/damages/create';
import { useCallback, useEffect, useState } from 'react';
import {
  InventoryLotWithVariant,
  getVariantLotsByWarehouseAndVariantIds,
} from 'services/inventory';
import { SearchIndex, getSearchData } from 'services/meilisearch';
import { IVariant, getVariant } from 'services/variant';
import { mergedVariantLot } from 'utilities/shared';

interface AddDamageProductDrawerProps {
  visible: boolean;
  damagedProducts: IDamagedProduct[];
  warehouseId?: string;
  onClose: () => void;
  onSubmit: (product: IDamagedProduct) => void;
}

type IOption = {
  id: string;
  name: string;
  sku: string;
  quantity: null | number;
};

export const styles = {
  stockText: { display: 'block', color: '#626262', fontSize: '0.88rem', fontWeight: 'bold' },
};

const { Text } = Typography;
let timeout: ReturnType<typeof setTimeout> | null;
let currentValue: string;

const fetchOptions = (value: string, callback: Function) => {
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }
  currentValue = value;

  const getOptions = async () => {
    const data = await getSearchData(SearchIndex.ProductVariant, value, { limit: 100 });

    if (currentValue === value && value !== '') {
      const options = data
        .map(
          (p) =>
            ({
              id: p.product_variant_id,
              sku: p.sku,
              name: p.name,
              quantity: null,
            }) as IOption,
        )
        .filter((op) => {
          const lowerCasedValue = value.toLowerCase();
          return (
            op.name.toLowerCase().includes(lowerCasedValue) ||
            op.sku.toLowerCase().includes(lowerCasedValue)
          );
        });
      callback(options);
    }
  };

  timeout = setTimeout(getOptions, 300);
};

export const AddDamageProductDrawer: React.FC<AddDamageProductDrawerProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<IVariant | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [lots, setLots] = useState<InventoryLotWithVariant[]>([]);
  const [isOptionLoading, setIsOptionLoading] = useState(false);

  const breakpoint = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  const fetchProductsQuantity = useCallback(async (warehouseId: string, ids: string[]) => {
    try {
      const response = await getVariantLotsByWarehouseAndVariantIds(warehouseId, ids);
      const products = mergedVariantLot(response);
      setLots(response);
      return products;
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
      return [];
    }
  }, []);

  const loadInitialOptions = useCallback(async () => {
    try {
      setIsOptionLoading(true);

      const data = await getSearchData(SearchIndex.ProductVariant, '', { limit: 20 });

      const baseOptions: IOption[] = data.map((p) => ({
        id: p.product_variant_id,
        sku: p.sku,
        name: p.name,
        quantity: null,
      }));

      if (props.warehouseId && baseOptions.length) {
        const ids = baseOptions.map((p) => p.id);
        const products = await fetchProductsQuantity(props.warehouseId, ids);

        const productsMap = new Map<string, (typeof products)[0]>();
        products.forEach((p) => productsMap.set(p.id, { ...p }));

        const enrichedOptions = baseOptions.map((option) => {
          const product = productsMap.get(option.id);
          return {
            ...option,
            quantity: product ? product.quantity : 0,
          };
        });

        setOptions(enrichedOptions);
      } else {
        setOptions(baseOptions);
      }
    } catch (err: any) {
      notification.error({
        message: 'Failed to load products',
        description: err.message,
      });
    } finally {
      setIsOptionLoading(false);
    }
  }, [props.warehouseId, fetchProductsQuantity]);

  useEffect(() => {
    loadInitialOptions();
  }, [loadInitialOptions]);

  useEffect(() => {
    form.setFields([
      {
        name: 'variant',
        value: selectedOption?.productVariantId,
      },
    ]);
  }, [form, selectedOption]);

  const handleProductOptionSearch = (newValue: string) => {
    setIsOptionLoading(true);

    fetchOptions(newValue, async (data: IOption[]) => {
      setOptions(data);

      if (props.warehouseId && data.length) {
        const ids = data.map((p) => p.id);
        const products = await fetchProductsQuantity(props.warehouseId, ids);
        const productsMap = new Map<string, (typeof products)[0]>();
        products.forEach((p) => productsMap.set(p.id, { ...p }));

        const options = data.map((option) => {
          const product = productsMap.get(option.id);
          if (product) {
            return {
              ...option,
              quantity: product.quantity,
            };
          } else {
            return {
              ...option,
              quantity: 0,
            };
          }
        });

        setOptions(options);
      }

      setIsOptionLoading(false);
    });
  };

  const handleProductOptionChange = (newValue: string) => {
    setSearchText(newValue);
  };

  const renderedProductOptions = options.map((p) => {
    // Option is disabled when product has quantity field and quantity is 0 or null
    const color = p.quantity === 0 || p.quantity === null ? '#A9A9A9' : '#626262';
    const title = `${p.sku} - ${p.name} (${p.quantity === null ? '⌛' : p.quantity})`;

    return {
      label: (
        <Typography.Text key={p.id} style={{ color }} title={title}>
          {`${p.sku} - ${p.name}`}{' '}
          {p.quantity === null && <Icons.LoadingOutlined style={{ marginLeft: '0.3rem' }} />}
          {typeof p.quantity === 'number' && `(${p.quantity})`}
        </Typography.Text>
      ),
      value: p.id,
      disabled: p.quantity === null || p.quantity === 0,
    };
  });

  const onSelectVariant = async (id: string) => {
    setIsLoading(true);
    form.resetFields(['quantity', 'lossAmount']);

    try {
      const variant = await getVariant(id);
      setSelectedOption(variant);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const renderedAvailableStock = !selectedOption ? null : (
    <ul>
      {lots
        .filter((lot) => lot.productVariant.id === selectedOption.productVariantId)
        .map((lot) => {
          return lot.quantity > 0 ? (
            <li key={lot.id}>
              <Text>
                {`${lot.quantity} units available | Bought @ BDT ${lot.unitPrice} on
                ${new Date(lot.lotEntryDate).toLocaleDateString()}`}
              </Text>
            </li>
          ) : null;
        })}
    </ul>
  );

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = selectedOption?.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFields = selectedOption?.customFields ?? [];

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  const renderNotFound = () => {
    if (isOptionLoading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Spin size="small" />
          <Typography.Text style={{ display: 'block', marginTop: 8, color: '#888' }}>
            Searching products...
          </Typography.Text>
        </div>
      );
    }

    return (
      <div style={{ padding: '12px', textAlign: 'center', color: '#888' }}>No products found</div>
    );
  };

  const onFinish = (values: any) => {
    if (!selectedOption) return;

    const variantId: string = values.variant;
    const quantity: number = values.quantity;
    const lossAmount: number = Number.parseFloat(values.lossAmount.toFixed(2));

    const totalQuantity = lots
      .filter((item) => item.productVariant.id === variantId)
      .reduce((acc, lot) => {
        acc += lot.quantity;
        return acc;
      }, 0);

    const isVariantExist = !!props.damagedProducts.find(
      (product) => product.productVariantId === variantId,
    );
    if (isVariantExist) {
      form.setFields([
        {
          name: 'variant',
          errors: [`Product is already added.`],
        },
      ]);
      return;
    }

    if (quantity === 0 || quantity > totalQuantity) {
      form.setFields([
        {
          name: 'quantity',
          errors: [`Quantity needs to be between 1 and ${totalQuantity}`],
        },
      ]);
      return;
    }

    props.onSubmit({ ...selectedOption, quantity, lossAmount });
    form.resetFields();
    setLots([]);
    setSelectedOption(null);
  };

  return (
    <Drawer
      title="Add Product"
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      visible={props.visible}
      onClose={props.onClose}
    >
      <Form
        layout="vertical"
        initialValues={{
          isActive: true,
        }}
        form={form}
        onFinish={onFinish}
      >
        <Form.Item
          label="Product"
          name="variant"
          rules={[{ required: true, message: 'Please select a product' }]}
        >
          <Select
            showSearch
            placeholder="Select a product"
            value={searchText}
            defaultActiveFirstOption={false}
            showArrow={false}
            filterOption={false}
            onSearch={handleProductOptionSearch}
            onChange={handleProductOptionChange}
            onSelect={onSelectVariant}
            options={renderedProductOptions}
            notFoundContent={renderNotFound()}
          />
        </Form.Item>

        {selectedOption && (
          <>
            <Divider />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
                <Typography.Text code>{selectedOption.productName}</Typography.Text>
              </div>

              {attributeElements}
              {customFieldElements}

              {readPurchasePrice?.can && (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography.Text strong>PURCHASE PRICE:</Typography.Text>
                  <Typography.Text>{selectedOption.purchasePrice}</Typography.Text>
                </div>
              )}
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography.Text strong>SELLING PRICE:</Typography.Text>
                <Typography.Text>{selectedOption.sellingPrice}</Typography.Text>
              </div>
            </div>
            <Divider />
          </>
        )}

        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Loss Amount"
          name="lossAmount"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} min={0} precision={2} />
        </Form.Item>

        <Space direction="vertical">
          <Text style={{ fontWeight: 'bolder' }}>* Available Stock</Text>
          {renderedAvailableStock}
        </Space>

        <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
          <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
            Add
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
